import { useLocation } from '@tanstack/react-router';
import { type FC, type PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthState, useAuth } from '@furality/frontend-stack';
import ErrorDisplay, { PortalLoginErrorContent } from '@/components/ErrorDisplay.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';

/**
 * Login gate, doesn't render contents unless logged in, redirect if not logged in
 *
 * @param children
 * @constructor
 */
const RequireLoggedIn: FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    if (auth.state === AuthState.NotLoggedIn && location.pathname !== '/') {
      auth.login();
    }
  }, [auth, location.pathname]);

  if (auth.state === AuthState.Error) {
    return (
      <div className="flex-center h-100 flex-grow-1">
        <ErrorDisplay title={t('f7.shared.login_error.title')} error={auth.error}>
          <PortalLoginErrorContent />
        </ErrorDisplay>
      </div>
    );
  }

  if (auth.state !== AuthState.LoggedIn) {
    return <PageLoading />;
  }

  // you can now use assertLoggedInAndDiscord
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export default RequireLoggedIn;
